import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDebouncedCallback } from 'use-debounce';
import { useLocation } from '~/context/LocationContext';

import { useStickyContext } from '~/context/StickyContext';
import { useCart } from '~/context/CartContext';

import Header from '~/layout/Header';
import Footer from '~/layout/Footer';
import Container from '~/layout/Container';

import GlobalStyles from '~/styles/GlobalStyles';
import { calcVw } from '~/styles/helpers';

import LocationPicker from '~/components/LocationPicker';
import Spinner from '~/components/Spinner';

import { scrollToElement, getElementFromHash } from '~/utilities/helpers';

export const FullWrapper = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto 1fr auto;
    min-height: 100vh;

    & > header {
        grid-row: 1;
    }

    & > main {
        grid-row: 2;
    }

    & > footer {
        grid-row: 4;
    }
`;

const LocationPickerWrapper = styled.div`
    position: sticky;
    top: 0;
    background-color: ${({ theme }) => theme.colors.text.blue};
    z-index: ${({ theme }) => theme.zIndex.userAccountDropdown};

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        z-index: ${({ theme }) => theme.zIndex.locationPicker};
        position: absolute;
        height: auto;
        left: 167px;
        top: 57px;
    }

    @media (min-width: 1400px) {
        left: 175px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.headerBreak}) {
        left: 180px;
        top: 65px;
    }

    @media (min-width: ${({ theme }) => theme.layout.maxWidth}) {
        left: calc(
            ${calcVw(50)} - (${({ theme }) => theme.layout.maxWidth} / 2) +
                ${({ theme }) => theme.layout.largeGutter} + 139px
        );
    }
`;

const Main = styled.main`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

export const hashLinkScroll = (hash, totalHeight) => {
    if (hash) {
        const element = getElementFromHash(hash);

        if (element) {
            scrollToElement(element, totalHeight);
        }
    }
};

const Default = ({ children }) => {
    const { hash, state: locationState } = useLocation();

    const { totalHeight } = useStickyContext();
    const { clearCart, items, loading: cartLoading } = useCart();

    const scrollToHash = useDebouncedCallback(
        (hash, totalHeight) => hashLinkScroll(hash, totalHeight),
        500
    );

    useEffect(() => {
        if (hash) {
            scrollToHash(hash, totalHeight);
        }
    }, [hash, scrollToHash, totalHeight]);

    useEffect(() => {
        if (locationState?.clearCart && items.length > 0) {
            clearCart(locationState.clearCustomer);
        }
    }, [locationState, clearCart, items]);

    return (
        <FullWrapper>
            <GlobalStyles />
            {cartLoading ? (
                <Spinner
                    customCss={{
                        position: 'fixed',
                        zIndex: 10
                    }}
                    fullWidth
                />
            ) : null}
            <Header />
            <LocationPickerWrapper>
                <LocationPicker />
            </LocationPickerWrapper>
            <Container>
                <Main id="main">{children}</Main>
            </Container>
            <Footer />
        </FullWrapper>
    );
};

Default.propTypes = {
    children: PropTypes.node.isRequired
};

export default Default;
